import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ControlProperty } from '../../../../../../common/models/forms.model';
import { TruckSize } from '../../../../../../common/constants/truck-size';
import { LocalDateRangeModel } from '../../../../../models';
import { AllocationViewActionAttachmentsForm } from '../allocation-attachments.model';
import { AllocationViewSealForm, AllocationViewSecurityModel } from '../allocation-security';
import { AllocationViewActionCarrierForm } from '../allocation-view-action-carrier.model';
import {
  AllocationViewActionQuestionForm,
  AllocationViewActionQuestionModel,
} from '../allocation-view-action-question.model';
import {
  AllocationViewActionRequestSlotModel,
  AllocationViewActionRequestUliModel,
} from '../allocation-view-action-request.model';
import { truckSealDataValidator } from './allocation-view-security-check-finished-validators';

export class AllocationViewSecurityFinishedModel extends AllocationViewSecurityModel {
  compartmentName: string;
  dockName: string;
  freeTextCarrierName: string;
  securityQuestions: AllocationViewSecurityFinishedQuestionsModel;
  sealed: boolean;
  truckPlate: string;
  truckPlateCountry: string;
  truckSize: TruckSize;
  customGoods: boolean;
  dangerousGoods: boolean;
  swapBody: boolean;
}

export class AllocationViewSecurityFinishedForm {
  constructor(
    model: AllocationViewSecurityFinishedModel,
    fb: UntypedFormBuilder,
    public generalInformation: UntypedFormGroup = fb.group(
      new AllocationViewSecurityFinishedGeneralInformationForm(model, fb),
    ),
    public securityQuestions: UntypedFormGroup = fb.group(
      new AllocationViewDepartureSecurityFinishedQuestionsForm(model, fb),
    ),
    public sealCheck: UntypedFormGroup = fb.group(new AllocationViewSecurityFinishedSealCheckForm(model, fb), {
      validators: truckSealDataValidator,
    }),
  ) {}
}

export class AllocationViewSecurityFinishedQuestionsModel {
  [key: string]: AllocationViewActionQuestionModel[];
}

export class AllocationViewSecurityFinishedGeneralInformationForm {
  constructor(
    model: AllocationViewSecurityFinishedModel,
    fb: UntypedFormBuilder,
    public hubId: ControlProperty<number> = model.hubId,
    public truckId: ControlProperty<number> = model.truckId,
    public truckPlateCountry: ControlProperty<string> = model.truckPlateCountry,
    public slot: ControlProperty<AllocationViewActionRequestSlotModel> = model.slot,
    public ulis: UntypedFormArray = fb.array([]),
    public carrier: UntypedFormGroup = fb.group(new AllocationViewActionCarrierForm(model.carrier)),
    public freeTextCarrierName: ControlProperty<string> = model.freeTextCarrierName,
    public dockName: ControlProperty<string> = model.dockName,
    public truckPlate: ControlProperty<string> = [model.truckPlate, []],
    public serviceTime: ControlProperty<LocalDateRangeModel> = [model.serviceTime, []],
    public firstName: ControlProperty<string> = [model.firstName, []],
    public lastName: ControlProperty<string> = [model.lastName, []],
    public compartmentName: ControlProperty<string> = [model.compartmentName, []],
    public propertyOfAnother: ControlProperty<boolean> = [model.propertyOfAnother, []],
    public remainingDrivingTime: ControlProperty<number> = [model.remainingDrivingTime, []],
    public customGoods: ControlProperty<boolean> = [model.customGoods, []],
    public dangerousGoods: ControlProperty<boolean> = [model.dangerousGoods, []],
    public swapBody: ControlProperty<boolean> = [model.swapBody, []],
  ) {
    model.ulis.forEach((uli: AllocationViewActionRequestUliModel) => this.ulis.push(new UntypedFormControl(uli)));
  }
}

export class AllocationViewDepartureSecurityFinishedQuestionsForm {
  constructor(model: AllocationViewSecurityFinishedModel, fb: UntypedFormBuilder) {
    const keys = Object.keys(model.securityQuestions).sort();

    keys.forEach((key: string, index: number) => {
      const isLast = keys.length - 1 === index;

      this[key] = fb.group(new AllocationViewSecurityFinishedQuestionForm(model, fb, key, isLast));
    });
  }
}

export class AllocationViewSecurityFinishedQuestionForm {
  securityCheckPassed: UntypedFormControl;

  constructor(
    model: AllocationViewSecurityFinishedModel,
    fb: UntypedFormBuilder,
    key: string,
    isLast: boolean,
    public questions: UntypedFormArray = fb.array([]),
  ) {
    model.securityQuestions[key].forEach((question: AllocationViewActionQuestionModel) => {
      questions.push(fb.group(new AllocationViewActionQuestionForm(question)));
    });

    if (isLast) {
      this.securityCheckPassed = fb.control(null, [Validators.required]);
    }
  }
}

export class AllocationViewSecurityFinishedSealCheckForm {
  constructor(
    model: AllocationViewSecurityFinishedModel,
    fb: UntypedFormBuilder,
    public sealed: ControlProperty<boolean> = [model.sealed, []],
    public truckSeal: UntypedFormGroup = fb.group(new AllocationViewSealForm(model.truckSeal)),
    public truckSize: ControlProperty<TruckSize> = [model.truckSize, []],
    public comments: UntypedFormGroup = fb.group(new AllocationViewActionAttachmentsForm(model.comments, fb)),
    public trailerSeal: UntypedFormGroup = fb.group(new AllocationViewSealForm(model.trailerSeal)),
  ) {}
}
